<template>
  <div class="page-card">
    <div class="page-header">
      <h2 class="page-title">艺人资料审核详情</h2>
      <el-button @click="goBack">返回列表</el-button>
    </div>

    <div class="profile-overview">
      <div class="avatar-box">
        <el-image
          v-if="avatarUrl"
          :src="avatarUrl"
          :preview-src-list="[avatarUrl]"
          fit="cover"
          class="avatar-image"
        >
          <div slot="error" class="avatar-placeholder">暂无头像</div>
        </el-image>
        <div v-else class="avatar-placeholder">暂无头像</div>
      </div>

      <div class="profile-main">
        <div class="profile-name-row">
          <span class="profile-name">{{ displayValue(getBaseInfo('artistName')) }}</span>
          <span class="profile-nickname">{{ displayValue(getBaseInfo('nickname')) }}</span>
          <el-tag size="small" :type="statusTagType(currentStatus)">
            {{ statusText(currentStatus) }}
          </el-tag>
        </div>
        <div class="profile-meta">
          <span>{{ genderText(getBaseInfo('gender')) }}</span>
          <span>{{ ageText(getBaseInfo('age')) }}</span>
          <span>{{ bodyText }}</span>
          <span>{{ displayValue(getBaseInfo('city')) }}</span>
        </div>
        <div class="contact-line">联系电话：{{ displayValue(getBaseInfo('contactMobile')) }}</div>
      </div>
    </div>

    <el-descriptions :column="2" border class="profile-descriptions">
      <el-descriptions-item label="艺人档案ID">
        {{ displayValue(getBaseInfo('artistId')) }}
      </el-descriptions-item>
      <el-descriptions-item label="接单状态">
        {{ availabilityText(getBaseInfo('availabilityStatus')) }}
      </el-descriptions-item>
      <el-descriptions-item label="通告偏好" :span="2">
        <div class="tag-line">
          <el-tag v-for="item in splitTags(getBaseInfo('noticeTypes'))" :key="`notice-${item}`" size="small">
            {{ item }}
          </el-tag>
          <span v-if="!splitTags(getBaseInfo('noticeTypes')).length">-</span>
        </div>
      </el-descriptions-item>
      <el-descriptions-item label="技能标签" :span="2">
        <div class="tag-line">
          <el-tag
            v-for="item in splitTags(getBaseInfo('skillTags'))"
            :key="`skill-${item}`"
            size="small"
            type="success"
          >
            {{ item }}
          </el-tag>
          <span v-if="!splitTags(getBaseInfo('skillTags')).length">-</span>
        </div>
      </el-descriptions-item>
      <el-descriptions-item label="档期说明" :span="2">
        <div class="pre-line">{{ displayValue(getBaseInfo('scheduleDesc')) }}</div>
      </el-descriptions-item>
      <el-descriptions-item label="经历说明" :span="2">
        <div class="pre-line">{{ displayValue(getBaseInfo('experienceDesc')) }}</div>
      </el-descriptions-item>
      <el-descriptions-item label="驳回/补充原因" :span="2">
        <div class="pre-line">{{ displayValue(getBaseInfo('rejectReason')) }}</div>
      </el-descriptions-item>
    </el-descriptions>

    <el-divider>资料素材</el-divider>
    <div v-if="visibleMaterials.length" class="material-grid">
      <div
        v-for="(item, index) in visibleMaterials"
        :key="item.id || item.mediaUrl || index"
        class="material-item"
      >
        <div class="material-label">{{ materialTypeText(item.mediaType, index) }}</div>
        <video
          v-if="isVideo(item)"
          class="material-media"
          :src="item.mediaUrl"
          :poster="item.coverUrl || ''"
          controls
          preload="metadata"
        />
        <el-image
          v-else
          :src="item.mediaUrl"
          :preview-src-list="previewImages"
          fit="cover"
          class="material-media"
        >
          <div slot="error" class="material-error">图片加载失败</div>
        </el-image>
      </div>
    </div>
    <el-empty v-else description="暂无资料素材" :image-size="88" />

    <el-divider>审核记录</el-divider>
    <el-table :data="detail.auditHistory || []" border>
      <el-table-column label="状态">
        <template slot-scope="{ row }">
          {{ statusText(row.status) }}
        </template>
      </el-table-column>
      <el-table-column prop="reason" label="原因" />
      <el-table-column prop="remark" label="备注" />
      <el-table-column prop="operatorName" label="处理人" />
      <el-table-column prop="operatedAt" label="处理时间" />
    </el-table>

    <div v-if="canOperate" class="action-bar">
      <el-button type="success" :loading="submitting" @click="approve">审核通过</el-button>
      <el-button type="danger" :loading="submitting" @click="openRejectDialog">驳回</el-button>
    </div>

    <el-dialog title="驳回原因" :visible.sync="rejectDialogVisible" width="480px" @closed="resetRejectDialog">
      <el-input v-model.trim="rejectReason" type="textarea" :rows="4" placeholder="请输入驳回原因" />
      <span slot="footer">
        <el-button @click="rejectDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="reject">确认驳回</el-button>
      </span>
    </el-dialog>

  </div>
</template>

<script>
import { MessageBox } from 'element-ui'
import {
  approveArtistProfile,
  getArtistProfileAuditDetail,
  rejectArtistProfile
} from '../../api/audit'
import { navigateWithFallback } from '../../utils/admin-navigation'
import { normalizeMediaItem, resolveMediaUrl } from '../../utils/format'

const DEFAULT_STATUS = 1
const DEFAULT_PAGE = 1
const MEDIA_TYPE_AVATAR = 1
const MEDIA_TYPE_IMAGE = 2
const MEDIA_TYPE_VIDEO = 3
const AUDIT_STATUS_PENDING = 1
const AUDIT_STATUS_APPROVED = 2
const AUDIT_STATUS_REJECTED = 3
const AUDIT_STATUS_SUPPLEMENT = 4

export default {
  name: 'ArtistProfileDetailPage',
  data() {
    return {
      detail: {},
      rejectDialogVisible: false,
      rejectReason: '',
      submitting: false
    }
  },
  computed: {
    currentStatus() {
      return Number(this.getBaseInfo('status')) || 0
    },
    canOperate() {
      return this.currentStatus === AUDIT_STATUS_PENDING
    },
    visibleMaterials() {
      return (this.detail.materials || []).map(normalizeMediaItem).filter(Boolean)
    },
    previewImages() {
      return this.visibleMaterials
        .filter(item => this.isImage(item))
        .map(item => item.mediaUrl)
        .filter(Boolean)
    },
    avatarUrl() {
      const baseAvatar = resolveMediaUrl(this.getBaseInfo('avatarUrl'))
      if (baseAvatar) return baseAvatar
      const avatarMaterial = this.visibleMaterials.find(item => Number(item.mediaType) === MEDIA_TYPE_AVATAR)
      return avatarMaterial ? avatarMaterial.mediaUrl : ''
    },
    bodyText() {
      const height = this.getBaseInfo('heightCm')
      const weight = this.getBaseInfo('weightKg')
      const heightText = height === 0 || height ? `${height} cm` : '-'
      const weightText = weight === 0 || weight ? `${weight} kg` : '-'
      return `${heightText} / ${weightText}`
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    getBaseInfo(key) {
      return this.detail.baseInfo ? this.detail.baseInfo[key] : ''
    },
    displayValue(value) {
      return value === 0 || value ? String(value) : '-'
    },
    splitTags(value) {
      if (!value) return []
      return String(value)
        .split(/[，,、/]/)
        .map(item => item.trim())
        .filter(Boolean)
    },
    ageText(value) {
      return value === 0 || value ? `${value} 岁` : '年龄未填写'
    },
    genderText(value) {
      if (value === 1 || value === '1') return '男'
      if (value === 2 || value === '2') return '女'
      if (value === 0 || value === '0') return '不限'
      return '性别未填写'
    },
    availabilityText(value) {
      if (value === 1 || value === '1') return '可接单'
      if (value === 2 || value === '2') return '暂不可接单'
      return this.displayValue(value)
    },
    statusText(status) {
      if (status === AUDIT_STATUS_PENDING) return '待审核'
      if (status === AUDIT_STATUS_APPROVED) return '已通过'
      if (status === AUDIT_STATUS_REJECTED) return '已驳回'
      if (status === AUDIT_STATUS_SUPPLEMENT) return '待补充资料'
      return status == null ? '-' : String(status)
    },
    statusTagType(status) {
      if (status === AUDIT_STATUS_PENDING) return 'warning'
      if (status === AUDIT_STATUS_APPROVED) return 'success'
      if (status === AUDIT_STATUS_REJECTED) return 'danger'
      if (status === AUDIT_STATUS_SUPPLEMENT) return 'info'
      return 'info'
    },
    materialTypeText(type, index) {
      if (Number(type) === MEDIA_TYPE_AVATAR) return '头像'
      if (Number(type) === MEDIA_TYPE_VIDEO || type === 'video' || this.isVideo({ mediaType: type })) {
        return `视频素材 ${index + 1}`
      }
      if (Number(type) === MEDIA_TYPE_IMAGE || type === 'image' || this.isImage({ mediaType: type })) {
        return `图片素材 ${index + 1}`
      }
      return `资料素材 ${index + 1}`
    },
    isImage(item) {
      if (!item) return false
      if (Number(item.mediaType) === MEDIA_TYPE_AVATAR || Number(item.mediaType) === MEDIA_TYPE_IMAGE) return true
      if (item.mediaType === 'image') return true
      return /\.(png|jpe?g|gif|webp|bmp)(\?.*)?$/i.test(item.mediaUrl || '')
    },
    isVideo(item) {
      if (!item) return false
      if (Number(item.mediaType) === MEDIA_TYPE_VIDEO) return true
      if (item.mediaType === 'video') return true
      return /\.(mp4|mov|webm|m4v|avi)(\?.*)?$/i.test(item.mediaUrl || '')
    },
    goBack() {
      const query = this.$route.query || {}
      if (query.from === 'dashboard') {
        navigateWithFallback(this, {
          path: '/dashboard',
          query: {
            auditType: query.fromType || 'artist-profile',
            auditPage: query.fromPage || String(DEFAULT_PAGE)
          }
        })
        return
      }
      navigateWithFallback(this, {
        path: '/audits',
        query: {
          type: query.fromType || 'artist-profile',
          status: query.fromStatus || String(DEFAULT_STATUS),
          page: query.fromPage || String(DEFAULT_PAGE)
        }
      })
    },
    async fetchData() {
      try {
        this.detail = await getArtistProfileAuditDetail(this.$route.params.id)
      } catch (error) {
        this.$message.error(error.message || '加载艺人资料详情失败')
      }
    },
    async approve() {
      if (this.submitting) return
      try {
        await MessageBox.confirm('确认审核通过该艺人资料吗？', '审核确认', {
          type: 'warning',
          confirmButtonText: '确认通过',
          cancelButtonText: '取消'
        })
      } catch (e) {
        return
      }
      try {
        this.submitting = true
        await approveArtistProfile(this.$route.params.id, { remark: '审核通过' })
        this.$message.success('操作成功')
        this.goBack()
      } catch (error) {
        this.$message.error(error.message || '审核通过失败')
      } finally {
        this.submitting = false
      }
    },
    openRejectDialog() {
      this.rejectDialogVisible = true
    },
    resetRejectDialog() {
      this.rejectReason = ''
    },
    async reject() {
      if (this.submitting) return
      if (!this.rejectReason) {
        this.$message.warning('请输入驳回原因')
        return
      }
      try {
        this.submitting = true
        await rejectArtistProfile(this.$route.params.id, {
          reason: this.rejectReason,
          remark: this.rejectReason
        })
        this.rejectDialogVisible = false
        this.$message.success('操作成功')
        this.goBack()
      } catch (error) {
        this.$message.error(error.message || '驳回失败')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.profile-overview {
  display: flex;
  gap: 18px;
  align-items: center;
  margin-bottom: 18px;
}

.avatar-box {
  width: 96px;
  height: 96px;
  flex: 0 0 96px;
}

.avatar-image,
.avatar-placeholder {
  width: 96px;
  height: 96px;
  border-radius: 8px;
}

.avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
}

.profile-main {
  min-width: 0;
  flex: 1;
}

.profile-name-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.profile-name {
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
}

.profile-nickname {
  color: #606266;
}

.profile-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  color: #606266;
  margin-bottom: 8px;
}

.contact-line {
  color: #303133;
}

.profile-descriptions {
  margin-top: 8px;
}

.tag-line {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.pre-line {
  white-space: pre-wrap;
  word-break: break-word;
}

.material-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.material-item {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
}

.material-label {
  margin-bottom: 10px;
  font-size: 14px;
  color: #303133;
}

.material-media {
  width: 100%;
  height: 180px;
  display: block;
  border-radius: 6px;
  background: #f5f7fa;
  object-fit: cover;
}

.material-error {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  background: #f5f7fa;
}

.action-bar {
  margin-top: 20px;
}
</style>
